import { useCallback, useEffect, useState } from "react";
import { Gavel, Loader2, MessageSquare, Plus, Vote } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const words = (s) => String(s || "").replace(/_/g, " ");
const STATUS = {
  open: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  voting: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  decided: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};
const OUTCOME = {
  approved: "text-emerald-600 dark:text-emerald-400",
  rejected: "text-red-600 dark:text-red-400",
  no_quorum: "text-amber-600 dark:text-amber-400",
  tied: "text-violet-600 dark:text-violet-400",
};

export const ECBoard = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async (keep) => {
    try {
      const res = await api.get("/ec/items");
      setData(res.data);
      if (keep) {
        const fresh = res.data.items.find((i) => i.item_ref === keep);
        if (fresh) setOpen(fresh);
      }
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, message, keep) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(message);
      if (res?.data?.item_ref) setOpen(res.data);
      await load(keep || res?.data?.item_ref);
      setDialog(null);
      setComment("");
      onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (error) return (
    <Panel title="EC white board" testId="panel-ec-closed">
      <p className="text-[12.5px] text-muted-foreground" data-testid="ec-board-refused">{error}</p>
    </Panel>
  );
  if (!data) return (
    <Panel title="EC white board" testId="panel-ec-loading">
      <p className="text-[12.5px] text-muted-foreground">Opening the white board…</p>
    </Panel>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
      <Panel title="EC white board" testId="panel-ec-board"
             kicker={`${data.items.length} matter(s) · ${data.visibility === "ec_only"
               ? "committee only" : "open to every voting member"}`}
             actions={data.may_raise && (
               <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                       data-testid="raise-ec-matter"
                       onClick={() => setDialog({ kind: "raise", category: "other" })}>
                 <Plus className="mr-1.5 h-3.5 w-3.5" /> Raise a matter
               </Button>
             )}>
        {data.items.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="ec-board-empty">
            Nothing is before the committee.
          </p>
        ) : (
          <div className="space-y-2">
            {data.items.map((i) => (
              <button key={i.item_ref} type="button" data-testid={`ec-item-${i.item_ref}`}
                      onClick={() => setOpen(i)}
                      className={`flex w-full flex-col gap-1 rounded-xl border px-3.5 py-3 text-left transition-colors duration-150 ${
                        open?.item_ref === i.item_ref
                          ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30"}`}>
                <span className="flex items-center justify-between gap-2">
                  <span className="text-[12.5px] font-medium text-foreground">{i.title}</span>
                  <Chip className={STATUS[i.status]}>{words(i.status)}</Chip>
                </span>
                <span className="text-[10.5px] text-muted-foreground">
                  {i.item_ref} · {i.category_label} · {i.raised_by} ({i.raised_as}) ·{" "}
                  {(i.comments || []).length} comment(s)
                  {i.status === "decided" && i.outcome?.status
                    ? ` · ${words(i.outcome.status)}` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {open ? (
        <Panel title={open.title} testId="panel-ec-item"
               kicker={`${open.item_ref} · ${open.category_label} · raised by ${open.raised_by} (${open.raised_as})`}
               actions={(
                 <div className="flex flex-wrap items-center gap-2">
                   <Chip className={STATUS[open.status]} testId="ec-item-status">
                     {words(open.status)}
                   </Chip>
                   {open.you_may_open && (
                     <Button size="sm" variant="outline" className="h-8 rounded-full"
                             data-testid="open-ec-voting" disabled={busy === "open"}
                             onClick={() => run("open",
                               () => api.post(`/ec/items/${open.item_ref}/open`, {}),
                               "The committee is voting", open.item_ref)}>
                       <Vote className="mr-1.5 h-3.5 w-3.5" /> Put it to the vote
                     </Button>
                   )}
                   {open.you_may_vote && (
                     <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                             data-testid="cast-ec-vote"
                             onClick={() => setDialog({ kind: "vote", decision: "for",
                               minute: "" })}>
                       <Gavel className="mr-1.5 h-3.5 w-3.5" /> Cast my vote
                     </Button>
                   )}
                   {open.you_may_close && (
                     <Button size="sm" variant="outline" className="h-8 rounded-full"
                             data-testid="close-ec-voting"
                             onClick={() => setDialog({ kind: "close", note: "" })}>
                       Close the voting
                     </Button>
                   )}
                 </div>
               )}>
          <p className="text-[12.5px] leading-relaxed text-muted-foreground"
             data-testid="ec-item-description">
            {open.description || "No paper was tabled with this matter."}
          </p>

          {open.status !== "open" && (
            <div className="mt-4 rounded-xl border border-border bg-muted/30 px-3.5 py-3"
                 data-testid="ec-item-voting">
              <p className="kicker">
                Voting · quorum {open.voting?.quorum} of {(open.voting?.eligible || []).length} ·{" "}
                {words(open.voting?.quorum_rule)} · {words(open.voting?.majority)} majority
              </p>
              {open.outcome?.status ? (
                <p className={`mt-1 text-[13px] font-medium ${OUTCOME[open.outcome.status] || ""}`}
                   data-testid="ec-item-outcome">
                  {words(open.outcome.status).toUpperCase()} — {open.outcome.for} for,{" "}
                  {open.outcome.against} against, {open.outcome.abstain} abstained of{" "}
                  {open.outcome.eligible} · turnout {open.outcome.turnout_percent}%
                  {open.decided_because ? ` · ${open.decided_because}` : ""}
                </p>
              ) : (
                <p className="mt-1 text-[12px] text-muted-foreground">
                  {(open.votes || []).length} of {(open.voting?.eligible || []).length} have voted.
                </p>
              )}
              <div className="mt-2 space-y-0.5">
                {(open.votes || []).map((v) => (
                  <p key={v.email} className="text-[11.5px] text-muted-foreground"
                     data-testid={`ec-vote-${v.email}`}>
                    <span className="text-foreground">{v.name}</span> ({v.office}) voted{" "}
                    {v.decision}{v.minute ? ` — ${v.minute}` : ""}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <p className="kicker">The discussion · every word stays on the record</p>
            <div className="mt-2 space-y-2" data-testid="ec-item-comments">
              {(open.comments || []).length === 0 && (
                <p className="text-[12px] text-muted-foreground">Nobody has spoken yet.</p>
              )}
              {(open.comments || []).map((c, index) => (
                <div key={`${c.email}-${c.at}-${index}`}
                     className="rounded-xl border border-border px-3 py-2">
                  <p className="text-[10.5px] text-muted-foreground">
                    {c.by} · {c.office} · {c.at.slice(0, 16).replace("T", " ")}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-foreground">{c.text}</p>
                </div>
              ))}
            </div>
            {open.you_may_comment && (
              <div className="mt-3 space-y-2">
                <Textarea rows={3} data-testid="ec-comment-input" value={comment}
                          placeholder="Speak to the matter"
                          onChange={(e) => setComment(e.target.value)} />
                <Button size="sm" variant="outline" className="h-8 rounded-full"
                        data-testid="ec-comment-save"
                        disabled={busy === "comment" || comment.trim().length < 2}
                        onClick={() => run("comment",
                          () => api.post(`/ec/items/${open.item_ref}/comments`,
                            { text: comment }), "Your comment is on the record",
                          open.item_ref)}>
                  {busy === "comment" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <MessageSquare className="mr-1.5 h-3.5 w-3.5" />} Add my comment
                </Button>
              </div>
            )}
            {!open.you_may_comment && !data.you_are_ec && (
              <p className="mt-3 text-[11.5px] text-muted-foreground" data-testid="ec-read-only">
                You can read everything the committee says and how each member voted. Only the
                committee may speak here.
              </p>
            )}
          </div>

          <div className="mt-4">
            <p className="kicker">The trail</p>
            <div className="mt-1.5 space-y-0.5" data-testid="ec-item-trail">
              {(open.trail || []).map((t, index) => (
                <p key={`${t.what}-${index}`} className="text-[11.5px] text-muted-foreground">
                  {t.what} · {t.by} · {t.at.slice(0, 16).replace("T", " ")}
                  {t.note ? ` — ${t.note}` : ""}
                </p>
              ))}
            </div>
          </div>
        </Panel>
      ) : (
        <Panel title="Pick a matter" testId="panel-ec-none">
          <p className="text-[12.5px] text-muted-foreground">
            Choose a matter on the left to read the paper, the discussion and the voting record.
          </p>
        </Panel>
      )}

      <Dialog open={dialog?.kind === "raise"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="raise-ec-dialog">
          <DialogHeader><DialogTitle>Raise a matter with the Executive Committee</DialogTitle>
            <DialogDescription>
              The committee discusses it here, then the President or the Secretary puts it to the
              vote.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">What is the matter?</Label>
              <Input data-testid="ec-title" value={dialog?.title || ""}
                     onChange={(e) => setDialog({ ...dialog, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Category</Label>
              <Select value={dialog?.category}
                      onValueChange={(v) => setDialog({ ...dialog, category: v })}>
                <SelectTrigger data-testid="ec-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(data.categories).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea rows={4} data-testid="ec-description" placeholder="The paper, in plain words"
                      value={dialog?.description || ""}
                      onChange={(e) => setDialog({ ...dialog, description: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="ec-raise-save"
                    disabled={busy === "raise" || (dialog?.title || "").trim().length < 6}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("raise", () => api.post("/ec/items", {
                      title: dialog.title, description: dialog.description || "",
                      category: dialog.category }), "The matter is before the committee")}>
              {busy === "raise" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Put it on the
              board
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "close"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="ec-close-dialog">
          <DialogHeader><DialogTitle>Close the voting</DialogTitle>
            <DialogDescription>
              {(open?.votes || []).length} of {(open?.voting?.eligible || []).length} have voted and
              quorum is {open?.voting?.quorum}. If the committee is short of quorum, say why you are
              closing it early — your words are recorded against the decision.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="ec-close-note"
                    placeholder="Why is the voting closing now?"
                    value={dialog?.note || ""}
                    onChange={(e) => setDialog({ ...dialog, note: e.target.value })} />
          <DialogFooter>
            <Button data-testid="ec-close-save" disabled={busy === "close"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("close",
                      () => api.post(`/ec/items/${open.item_ref}/close`,
                        { note: dialog.note || "" }), "The matter is decided", open.item_ref)}>
              {busy === "close" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Close it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "vote"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="ec-vote-dialog">
          <DialogHeader><DialogTitle>Cast your vote</DialogTitle>
            <DialogDescription>
              Your name, your office and anything you write here are recorded against this matter.
            </DialogDescription></DialogHeader>
          <div className="flex gap-2">
            {["for", "against", "abstain"].map((d) => (
              <Button key={d} size="sm" variant={dialog?.decision === d ? "default" : "outline"}
                      className="h-8 flex-1 rounded-full" data-testid={`ec-vote-${d}`}
                      onClick={() => setDialog({ ...dialog, decision: d })}>
                {d}
              </Button>
            ))}
          </div>
          <Textarea rows={3} data-testid="ec-vote-minute" placeholder="Anything for the minute"
                    value={dialog?.minute || ""}
                    onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          <DialogFooter>
            <Button data-testid="ec-vote-save" disabled={busy === "vote"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("vote",
                      () => api.post(`/ec/items/${open.item_ref}/vote`,
                        { decision: dialog.decision, minute: dialog.minute || "" }),
                      "Your vote is recorded", open.item_ref)}>
              {busy === "vote" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record my vote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
